"use strict";
/**
 * degreePlanPrint — Accessible client-side PDF export for student degree plans.
 *
 * `buildDegreePlanHtml` is a pure function that returns a fully-styled,
 * semantically-structured HTML document.  `printDegreePlan` injects that HTML
 * into a hidden iframe and calls contentWindow.print() to open the native
 * print dialog so the user can "Save as PDF".
 *
 * Using a hidden iframe (rather than window.open) avoids popup blockers: only
 * window.open() requires a synchronous user gesture; contentWindow.print()
 * does not.  The iframe is removed from the DOM after printing completes.
 *
 * Modern browsers produce tagged PDFs from semantic HTML, meeting WCAG 2.1 AA
 * (proper title, alt text, heading hierarchy, table semantics).
 *
 * No HTTP request is made → no auth/token exposure (relevant for the
 * Salesforce iframe path).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildDegreePlanHtml = buildDegreePlanHtml;
exports.printDegreePlan = printDegreePlan;
const tenants_1 = require("../config/tenants");
const DISCLAIMER = 'Please note — dates and program sequence are subject to change.';
function esc(text) {
    if (text == null)
        return '';
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
function formatLongDate(input) {
    if (!input)
        return '';
    const date = input instanceof Date ? input : (() => {
        // Treat bare YYYY-MM-DD as local-midnight, not UTC, to avoid off-by-one display
        if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
            const [y, m, d] = input.split('-').map(Number);
            return new Date(y, m - 1, d);
        }
        return new Date(input);
    })();
    if (isNaN(date.getTime()))
        return '';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}
function formatTermDate(date, confirmed) {
    if (!date || isNaN(date.getTime()))
        return '';
    if (confirmed) {
        // MM/dd/yyyy
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${m}/${d}/${date.getFullYear()}`;
    }
    // MMM yyyy
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}
function studentDisplayName(plan) {
    var _a, _b, _c, _d, _e, _f;
    const first = (_c = (_b = (_a = plan.profile) === null || _a === void 0 ? void 0 : _a.firstName) === null || _b === void 0 ? void 0 : _b.trim()) !== null && _c !== void 0 ? _c : '';
    const last = (_f = (_e = (_d = plan.profile) === null || _d === void 0 ? void 0 : _d.lastName) === null || _e === void 0 ? void 0 : _e.trim()) !== null && _f !== void 0 ? _f : '';
    const joined = `${first} ${last}`.trim();
    return joined;
}
function programDisplayName(plan) {
    var _a, _b, _c, _d;
    // Per Student Services: render the program name only (drop the legacy
    // " Plan" suffix).  Prefer cohort.program (the authoritative tag used in
    // the legacy export); fall back to academicProgram.name.
    return ((_b = (_a = plan.cohort) === null || _a === void 0 ? void 0 : _a.program) === null || _b === void 0 ? void 0 : _b.trim()) || ((_d = (_c = plan.academicProgram) === null || _c === void 0 ? void 0 : _c.name) === null || _d === void 0 ? void 0 : _d.trim()) || '';
}
/**
 * Builds the complete, self-contained HTML document for printing a degree plan.
 * Pure — no DOM/window access; safe to call from Node for sample generation.
 */
function buildDegreePlanHtml(options) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const { degreePlan, termPlans, graduationDate, cohortStartTermName } = options;
    const branding = (_a = options.branding) !== null && _a !== void 0 ? _a : (0, tenants_1.getTenantBranding)((_b = degreePlan.organization) === null || _b === void 0 ? void 0 : _b.tenantId);
    const universityName = (_e = (_c = branding === null || branding === void 0 ? void 0 : branding.displayName) !== null && _c !== void 0 ? _c : (_d = degreePlan.organization) === null || _d === void 0 ? void 0 : _d.name) !== null && _e !== void 0 ? _e : '';
    const docTitle = universityName
        ? `${universityName} Program Plan`
        : 'Program Plan';
    const studentName = studentDisplayName(degreePlan);
    const program = programDisplayName(degreePlan);
    const specialization = (_h = (_g = (_f = degreePlan.cohort) === null || _f === void 0 ? void 0 : _f.specialization) === null || _g === void 0 ? void 0 : _g.trim()) !== null && _h !== void 0 ? _h : '';
    const track = (_l = (_k = (_j = degreePlan.cohort) === null || _j === void 0 ? void 0 : _j.track) === null || _k === void 0 ? void 0 : _k.trim()) !== null && _l !== void 0 ? _l : '';
    const cohortTerm = (cohortStartTermName === null || cohortStartTermName === void 0 ? void 0 : cohortStartTermName.trim()) || ((_o = (_m = degreePlan.cohort) === null || _m === void 0 ? void 0 : _m.name) === null || _o === void 0 ? void 0 : _o.trim()) || '';
    const gradDate = formatLongDate(graduationDate || degreePlan.graduationDate);
    const lastModified = ((_p = degreePlan.audit) === null || _p === void 0 ? void 0 : _p.dateModified)
        ? formatLongDate(new Date(degreePlan.audit.dateModified))
        : '';
    const sortedTerms = [...termPlans].sort((a, b) => a.position - b.position);
    const tableSummaryText = program
        ? `This table outlines the required courses for the ${esc(program)} program by term, including course codes and titles.`
        : 'This table outlines the required courses by term, including course codes and titles.';
    const termSections = sortedTerms
        .map((tp, idx) => {
        const termName = tp.term.clientName || tp.term.name || '';
        const startDate = formatTermDate(tp.term.startDate, tp.term.confirmedDate);
        const endDate = formatTermDate(tp.term.endDate, tp.term.confirmedDate);
        const courseRows = tp.courses
            .map((cp) => {
            const c = cp.course;
            const code = esc(c.displayCode || c.code);
            // Match legacy fallback chain: displayName → shortDescription → longDescription
            const title = esc(c.displayName || c.shortDescription || c.longDescription || c.name);
            return `
            <tr>
              <td>${code}</td>
              <td>${title}</td>
            </tr>`;
        })
            .join('');
        const hId = `term-${idx + 1}-heading`;
        const dateRange = startDate && endDate ? `${startDate} – ${endDate}` : (startDate || endDate);
        return `
        <section aria-labelledby="${hId}" class="term-section">
          <h3 id="${hId}">${esc(termName)}</h3>
          ${dateRange ? `<p class="term-dates">${esc(dateRange)}</p>` : ''}
          <table>
            <thead>
              <tr>
                <th scope="col">Code</th>
                <th scope="col">Course Title</th>
              </tr>
            </thead>
            <tbody>
              ${courseRows || '<tr><td>No courses planned.</td><td></td></tr>'}
            </tbody>
          </table>
        </section>`;
    })
        .join('');
    const metadataEntries = [];
    if (studentName)
        metadataEntries.push(['Student', studentName]);
    if (cohortTerm)
        metadataEntries.push(['Cohort', cohortTerm]);
    if (gradDate)
        metadataEntries.push(['Graduation Date', gradDate]);
    if (lastModified)
        metadataEntries.push(['Plan Last Modified', lastModified]);
    const metadataHtml = metadataEntries
        .map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`)
        .join('\n          ');
    const logoHtml = (branding === null || branding === void 0 ? void 0 : branding.logo)
        ? `<img class="logo" src="${esc(branding.logo.src)}" alt="${esc(branding.logo.alt)}" />`
        : '';
    const org = degreePlan.organization;
    const calendarLink = (org === null || org === void 0 ? void 0 : org.academicCalendarLink)
        ? `<li><a href="${esc(org.academicCalendarLink)}">${esc(org.academicCalendarDescription || 'Academic Calendar')}</a></li>`
        : '';
    const courseDescLink = (org === null || org === void 0 ? void 0 : org.courseDescriptionsLink)
        ? `<li><a href="${esc(org.courseDescriptionsLink)}">Course Descriptions</a></li>`
        : '';
    const linksHtml = (calendarLink || courseDescLink)
        ? `<nav aria-label="Reference links"><ul class="reference-links">${calendarLink}${courseDescLink}</ul></nav>`
        : '';
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${esc(docTitle)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { font-size: 14px; }
    body {
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      color: #1a1a1a;
      background: #ffffff;
      padding: 1.5rem 2rem;
      line-height: 1.5;
    }
    p.table-summary {
      font-size: 0.78rem;
      color: #555;
      font-style: italic;
      margin: 0.1rem 0 0.75rem;
    }
    header.plan-header {
      display: flex;
      align-items: center;
      gap: 1.25rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid #1a1a1a;
      margin-bottom: 1rem;
    }
    .plan-header .logo {
      flex: 0 0 auto;
      width: 90px;
      height: 90px;
      object-fit: contain;
    }
    .plan-header .titles {
      flex: 1 1 auto;
    }
    .plan-header h1 {
      font-size: 1.4rem;
      line-height: 1.2;
      color: #1a1a1a;
      margin-bottom: 0.15rem;
    }
    .plan-header .program {
      font-size: 1.25rem;
      font-weight: 600;
      color: #111;
    }
    .plan-header .specialization {
      font-size: 0.95rem;
      color: #444;
    }
    .plan-header .track {
      font-size: 0.9rem;
      color: #555;
    }
    section.plan-meta {
      margin-bottom: 1.25rem;
    }
    section.plan-meta dl {
      display: grid;
      grid-template-columns: max-content 1fr;
      column-gap: 1.25rem;
      row-gap: 0.25rem;
      font-size: 0.9rem;
    }
    section.plan-meta dt { font-weight: 600; color: #111; }
    section.plan-meta dd { color: #333; }
    h2 {
      font-size: 1.1rem;
      margin: 1.25rem 0 0.5rem;
      padding-bottom: 0.25rem;
      border-bottom: 1px solid #d1d5db;
      color: #111;
    }
    h3 {
      font-size: 1rem;
      margin: 1rem 0 0.25rem;
      color: #222;
    }
    .term-dates {
      font-size: 0.85rem;
      color: #555;
      margin-bottom: 0.35rem;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 0.5rem;
      font-size: 0.875rem;
    }
    th, td {
      border: 1px solid #d1d5db;
      padding: 0.35rem 0.6rem;
      text-align: left;
      vertical-align: top;
    }
    thead th {
      background: #f3f4f6;
      font-weight: 600;
      color: #111;
    }
    tfoot th, tfoot td {
      background: #f9fafb;
      font-weight: 600;
    }
    tbody tr:nth-child(even) { background: #fafafa; }
    .disclaimer {
      margin: 1rem 0 0.5rem;
      font-style: italic;
      font-size: 0.85rem;
      color: #444;
    }
    .reference-links {
      list-style: none;
      padding: 0;
      margin-top: 0.5rem;
      font-size: 0.9rem;
    }
    .reference-links li { margin: 0.15rem 0; }
    .reference-links a { color: #0b5cab; text-decoration: underline; }
    @media print {
      body { padding: 0.5in; }
      /* Suppress background fills in print — Pdfium tags background rectangles as
         direct children of their structure element (TR, TH). When even rows have a
         background and odd rows don't, TRs end up with different child counts, which
         fails Acrobat's Table Regularity check (PDF/UA requires every TR in a table
         to have the same number of cells). */
      tbody tr:nth-child(even),
      thead th { background: transparent; }
      header.plan-header,
      section.term-section { page-break-inside: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      .reference-links a { color: #0b5cab; }
    }
    @page {
      size: letter;
      /* Zero margin removes the browser-injected URL/date header and footer.
         Visual margins are provided by the @media print body padding below. */
      margin: 0;
    }
  </style>
</head>
<body>
  <header class="plan-header">
    ${logoHtml}
    <div class="titles">
      <h1>${esc(universityName)}</h1>
      ${program ? `<p class="program">${esc(program)}</p>` : ''}
      ${specialization ? `<p class="specialization">${esc(specialization)}</p>` : ''}
      ${track ? `<p class="track">${esc(track)}</p>` : ''}
    </div>
  </header>

  <section class="plan-meta" aria-labelledby="plan-details-heading">
    <h2 id="plan-details-heading">Plan Details</h2>
    <dl>
          ${metadataHtml}
    </dl>
  </section>

  <section aria-labelledby="course-schedule-heading">
    <h2 id="course-schedule-heading">Course Schedule</h2>
    <p class="table-summary">${tableSummaryText}</p>
    ${termSections || '<p>No terms planned.</p>'}
  </section>

  <p class="disclaimer">${esc(DISCLAIMER)}</p>

  ${linksHtml}
</body>
</html>`;
}
/**
 * Writes the degree-plan HTML into a hidden iframe and triggers the native
 * print dialog via contentWindow.print().
 *
 * Using a hidden iframe instead of window.open avoids popup blockers entirely:
 * window.print() and contentWindow.print() do not require a user gesture, so
 * async work (like the cohort-term lookup in handleExport) can happen before
 * calling this function without losing the ability to open the print dialog.
 *
 * The iframe is removed from the DOM automatically after printing.
 */
function printDegreePlan(options) {
    const html = buildDegreePlanHtml(options);
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;left:-9999px;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const cleanup = () => {
        try {
            document.body.removeChild(iframe);
        }
        catch ( /* noop */_a) { /* noop */ }
    };
    const iframeWin = iframe.contentWindow;
    if (!iframeWin) {
        cleanup();
        throw new Error('Unable to create print frame.');
    }
    iframeWin.document.open();
    iframeWin.document.write(html);
    iframeWin.document.close();
    iframeWin.onafterprint = cleanup;
    const triggerPrint = () => {
        try {
            iframeWin.focus();
            iframeWin.print();
        }
        catch (_a) {
            // Ignore — browser may restrict print() in embedded contexts.
        }
    };
    // Always wait for the iframe's load event rather than checking readyState.
    // document.readyState becomes 'complete' immediately after document.close()
    // (HTML parse done), but hashed asset images are still being fetched at that
    // point.  iframeWin.onload fires only after all sub-resources — including
    // the tenant logo JPG — are fully loaded, so the image appears in the PDF.
    iframeWin.onload = triggerPrint;
}
